import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Truck, TrendingDown, Package, ShoppingCart, ChevronRight } from "lucide-react";

export default function PurchasingDashboard() {
  const [summary, setSummary] = useState(null);
  const [vp, setVp] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/purchasing/dashboard/summary"),
      api.get("/purchasing/vendor-performance"),
      api.get("/purchasing/low-stock"),
      api.get("/purchasing/vendors"),
      api.get("/purchasing/products"),
      api.get("/purchasing/brands"),
    ]).then(([s, v, ls, vs, ps, bs]) => {
      setSummary(s.data); setVp(v.data); setLowStock(ls.data);
      setVendors(vs.data); setProducts(ps.data); setBrands(bs.data);
    }).catch(() => {});
  }, []);

  const vendorName = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.name])), [vendors]);
  const productName = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);
  const brandName = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const c = summary?.counts || {};

  const createPr = async (a) => {
    if (!window.confirm(`Create a Purchase Request for ${a.shortage} ${a.unit_of_measure || "units"} of ${a.product_name} (${a.brand_name})?`)) return;
    try {
      await api.post("/purchasing/purchase-requests", {
        items: [{ product_id: a.product_id, brand_id: a.brand_id, quantity: a.shortage, notes: `Auto-created from low-stock alert (min ${a.min_stock}, on hand ${a.current_stock})` }],
        remarks: "Auto-created from Low-Stock Alert on dashboard",
      });
      alert("Purchase Request created. Approvals workflow lands in Phase 4.");
    } catch (e) {
      alert(e.response?.data?.detail || "Create PR failed");
    }
  };

  return (
    <div data-testid="pv-dashboard-page">
      <PageHeader eyebrow="Purchasing" title="Purchasing Dashboard" subtitle="Pending POs, late deliveries, price alerts, vendor performance, and low-stock signals — all in one view." />
      <div className="p-4 sm:p-8 space-y-6">
        {/* Top counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Counter label="Pending POs" value={c.pending_pos ?? "—"} sub={c.pending_po_value != null ? inr(c.pending_po_value) : ""} to="/purchasing/purchase-orders" icon={ShoppingCart} testid="pv-c-pending" />
          <Counter label="Late Deliveries" value={c.late_deliveries ?? "—"} tone={c.late_deliveries ? "danger" : "ok"} to="/purchasing/purchase-orders" icon={Clock} testid="pv-c-late" />
          <Counter label="Today's Deliveries" value={c.today_deliveries ?? "—"} to="/purchasing/purchase-orders" icon={Truck} testid="pv-c-today" />
          <Counter label="Total PO Value" value={c.total_po_value != null ? inr(c.total_po_value) : "—"} mono to="/purchasing/purchase-orders" icon={TrendingDown} testid="pv-c-value" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Late Deliveries */}
          <Card title="Late Deliveries" icon={<Clock className="w-4 h-4 text-[#DC2626]" />} link={{ to: "/purchasing/purchase-orders", label: "All POs" }} testid="pv-card-late">
            {summary?.late_deliveries?.length ? (
              <ul className="divide-y divide-[#F3F4F6]">
                {summary.late_deliveries.map((p) => (
                  <li key={p.id} className="py-3 flex items-center justify-between gap-2 text-xs">
                    <div>
                      <div className="font-mono font-bold">{p.po_number}</div>
                      <div className="text-[#4B5563]">{vendorName[p.vendor_id] || "—"} · Expected {p.expected_delivery_date}</div>
                    </div>
                    <span className="uppercase tracking-widest text-[9px] font-bold px-2 py-0.5 bg-[#FEE2E2] text-[#991B1B]">{p.status}</span>
                  </li>
                ))}
              </ul>
            ) : <Empty text="No late deliveries. 👌" />}
          </Card>

          {/* Today's Deliveries */}
          <Card title="Today's Expected Deliveries" icon={<Truck className="w-4 h-4 text-[#002FA7]" />} testid="pv-card-today">
            {summary?.today_deliveries?.length ? (
              <ul className="divide-y divide-[#F3F4F6]">
                {summary.today_deliveries.map((p) => (
                  <li key={p.id} className="py-3 flex items-center justify-between gap-2 text-xs">
                    <div>
                      <div className="font-mono font-bold">{p.po_number}</div>
                      <div className="text-[#4B5563]">{vendorName[p.vendor_id] || "—"}</div>
                    </div>
                    <div className="text-xs font-mono">{inr(p.grand_total)}</div>
                  </li>
                ))}
              </ul>
            ) : <Empty text="Nothing scheduled for today." />}
          </Card>

          {/* Recent Price Updates */}
          <Card title="Recent Price Updates" icon={<TrendingDown className="w-4 h-4 text-[#059669]" />} link={{ to: "/purchasing/price-enquiries", label: "Register" }} testid="pv-card-updates">
            {summary?.recent_price_updates?.length ? (
              <ul className="divide-y divide-[#F3F4F6]">
                {summary.recent_price_updates.map((e) => (
                  <li key={e.id} className="py-2.5 text-xs flex items-center justify-between gap-2">
                    <div>
                      <div className="font-semibold">{productName[e.product_id]} · <span className="text-[#4B5563]">{brandName[e.brand_id] || "—"}</span></div>
                      <div className="text-[#9CA3AF]">{vendorName[e.vendor_id] || "—"} · {e.enquiry_date}</div>
                    </div>
                    <div className="font-mono">{inr(e.quoted_price)}</div>
                  </li>
                ))}
              </ul>
            ) : <Empty text="No recent enquiries." />}
          </Card>

          {/* Lowest Price Alerts */}
          <Card title="Lowest Price (last 30 d)" icon={<TrendingDown className="w-4 h-4 text-[#F59E0B]" />} link={{ to: "/purchasing/comparison", label: "Compare" }} testid="pv-card-lowest">
            {summary?.lowest_price_alerts?.length ? (
              <ul className="divide-y divide-[#F3F4F6]">
                {summary.lowest_price_alerts.map((e) => (
                  <li key={e.id} className="py-2.5 text-xs flex items-center justify-between gap-2">
                    <div>
                      <div className="font-semibold">{productName[e.product_id]}</div>
                      <div className="text-[#9CA3AF]">{vendorName[e.vendor_id] || "—"} · {brandName[e.brand_id] || "—"}</div>
                    </div>
                    <div className="font-mono text-[#10B981]">{inr(e.quoted_price)}</div>
                  </li>
                ))}
              </ul>
            ) : <Empty text="No recent quotes to compare." />}
          </Card>
        </div>

        {/* Low-Stock */}
        <Card
          title="Low-Stock Alerts"
          icon={<AlertTriangle className="w-4 h-4 text-[#DC2626]" />}
          link={{ to: "/purchasing/brands", label: "Set thresholds" }}
          testid="pv-card-low-stock"
        >
          {lowStock.length ? (
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB]">
                <tr>{["Product", "Brand", "On hand", "Min", "Shortage", ""].map((h, i) =>
                  <th key={i} className="text-left label-eyebrow !text-[10px] px-3 py-2">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {lowStock.map((a) => (
                  <tr key={`${a.product_id}|${a.brand_id}`} className="border-t border-[#F3F4F6]" data-testid={`pv-low-row-${a.product_id}-${a.brand_id}`}>
                    <td className="px-3 py-2 text-xs font-semibold">{a.product_name}</td>
                    <td className="px-3 py-2 text-xs">{a.brand_name}</td>
                    <td className="px-3 py-2 text-xs font-mono">{a.current_stock} {a.unit_of_measure}</td>
                    <td className="px-3 py-2 text-xs font-mono">{a.min_stock}</td>
                    <td className="px-3 py-2 text-xs font-mono text-[#DC2626]">-{a.shortage}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => createPr(a)}
                        className="text-xs uppercase tracking-widest font-bold text-[#002FA7] hover:underline"
                        data-testid={`pv-low-pr-${a.brand_id}`}
                      >Create PR →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty text="All stock levels healthy. Set min_stock on brands to enable alerts." />}
        </Card>

        {/* Vendor Performance */}
        <Card
          title="Vendor Performance"
          icon={<Package className="w-4 h-4 text-[#4B5563]" />}
          link={{ to: "/purchasing/vendor-performance", label: "Full report" }}
          testid="pv-card-vp"
        >
          {vp.length ? (
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB]">
                <tr>{["Vendor", "POs", "Delivered", "Pending", "Delayed", "On-time %", "Avg days", "Value", "Score"].map((h, i) =>
                  <th key={i} className="text-left label-eyebrow !text-[10px] px-3 py-2">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {vp.slice(0, 8).map((v) => (
                  <tr key={v.vendor_id} className="border-t border-[#F3F4F6]" data-testid={`pv-vp-row-${v.vendor_id}`}>
                    <td className="px-3 py-2 text-xs font-semibold">{vendorName[v.vendor_id] || "—"}</td>
                    <td className="px-3 py-2 text-xs">{v.total_pos}</td>
                    <td className="px-3 py-2 text-xs">{v.delivered_pos}</td>
                    <td className="px-3 py-2 text-xs">{v.pending_pos}</td>
                    <td className={`px-3 py-2 text-xs ${v.delayed_pos ? "text-[#DC2626] font-bold" : ""}`}>{v.delayed_pos}</td>
                    <td className="px-3 py-2 text-xs">{v.on_time_pct != null ? `${v.on_time_pct}%` : "—"}</td>
                    <td className="px-3 py-2 text-xs">{v.avg_delivery_days != null ? `${v.avg_delivery_days} d` : "—"}</td>
                    <td className="px-3 py-2 text-xs font-mono">{inr(v.total_value)}</td>
                    <td className="px-3 py-2 text-xs font-bold" style={{ color: v.score >= 75 ? "#059669" : v.score >= 50 ? "#D97706" : "#DC2626" }}>{v.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty text="No vendors with POs yet." />}
        </Card>
      </div>
    </div>
  );
}

function Counter({ label, value, sub, tone, mono, to, icon: Icon, testid }) {
  const border = tone === "danger" && value > 0 ? "border-[#DC2626]" : "border-[#E5E7EB]";
  return (
    <Link to={to} className={`flat-card p-4 hover:bg-[#F9FAFB] transition-colors border ${border}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#9CA3AF]" />}
      </div>
      <div className={`text-3xl font-black tracking-tight mt-2 ${mono ? "font-mono" : ""} ${tone === "danger" && value > 0 ? "text-[#DC2626]" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#9CA3AF] font-mono mt-1">{sub}</div>}
    </Link>
  );
}

function Card({ title, icon, link, children, testid }) {
  return (
    <div className="flat-card" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <div className="flex items-center gap-2 label-eyebrow">{icon} {title}</div>
        {link && <Link to={link.to} className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline flex items-center gap-1">{link.label} <ChevronRight className="w-3 h-3" /></Link>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center text-xs text-[#9CA3AF] py-6">{text}</div>;
}
